import { useApolloClient } from '@apollo/client/react';
import { createContext, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSocket } from '../../providers/SocketProvider';
import { CompanySessionResetOptions, resetCompanySession } from '../companySession';

/**
 * Hides the routed pages while the page moves company. Provided by the viewer provider, which renders the full page
 * loading state in their place, so a reset started from any page (act-as dialog, banner, landing) can hide them.
 */
export const CompanyChangeContext = createContext<((inProgress: boolean) => void) | undefined>(undefined);

/**
 * Moves the page onto the session's new company after an act-as, a stop or a refusal from another tab. Every cached
 * object and the socket's handshake belong to the previous company, so both are dropped rather than refetched, and
 * the user lands on the app home, the one page that exists in every company.
 */
export function useCompanySessionReset(): (
  nextCompanyId: string | undefined,
  options?: CompanySessionResetOptions,
) => Promise<void> {
  const client = useApolloClient();
  const { reconnect } = useSocket();
  const navigate = useNavigate();
  const setInProgress = useContext(CompanyChangeContext);

  if (!setInProgress) {
    throw new Error('useCompanySessionReset must be used within a ViewerProvider');
  }

  return useCallback(
    (nextCompanyId: string | undefined, options?: CompanySessionResetOptions) =>
      resetCompanySession(
        {
          resetStore: () => client.resetStore(),
          navigate: (path) => navigate(path),
          reconnect,
          setInProgress,
        },
        nextCompanyId,
        options,
      ),
    [client, navigate, reconnect, setInProgress],
  );
}
